import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Package, Check, ArrowLeft, Loader2, AlertCircle, User, Calendar, Shield, DollarSign
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { API } from "../config/api";

const POLE_PRICES = {
  "9'0": 60, "10'0": 60, "10'6": 65, "11'0": 65, "11'6": 65,
  "12'1": 65, "12'7": 70, "13'1": 70, "13'7": 70, "14'1": 70,
  "14'7": 70, "15'1": 85, "15'7": 85, "16'1": 85, "16'5": 85, "16'9": 85,
};
const INSURANCE_PRICE = 10;

function getPolePrice(length) {
  if (!length) return 0;
  if (POLE_PRICES[length]) return POLE_PRICES[length];
  const match = String(length).match(/(\d+)/);
  if (match) {
    const feet = parseInt(match[1]);
    if (feet >= 15) return 85;
    if (feet >= 12) return 70;
    if (feet >= 10) return 65;
    return 60;
  }
  return 70;
}

export default function SelectAlternatePage() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selections, setSelections] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [includeInsurance, setIncludeInsurance] = useState(true);

  useEffect(() => {
    const fetchRequest = async () => {
      if (!requestId) { setError("No request ID provided"); setLoading(false); return; }
      try {
        const response = await axios.get(`${API}/request/${requestId}`);
        const data = response.data;
        if (data?.request) {
          setRequest({
            ...data.request,
            request_id: data.request.id,
            requester_name: data.request.user_name,
            requester_email: data.request.user_email,
            desired_poles: data.desired_poles || []
          });
          if (data.request.insurance !== undefined) setIncludeInsurance(data.request.insurance);
          const initial = {};
          (data.desired_poles || []).forEach((dp, idx) => {
            if (dp.exact_available && dp.exact_matches?.[0]) {
              initial[idx] = dp.exact_matches[0].pole_id;
            }
          });
          setSelections(initial);
        } else {
          setError("Request not found");
        }
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchRequest();
  }, [requestId]);

  const selectedPoles = useMemo(() => {
    if (!request?.desired_poles) return [];
    return request.desired_poles.map((dp, idx) => {
      const poleId = selections[idx];
      if (!poleId || poleId === -1) return null;
      const match = dp.exact_matches?.find(p => p.pole_id === poleId)
        || dp.similar_matches?.find(p => p.pole_id === poleId);
      return match ? { ...match, length: match.length || dp.requested?.length, weight: match.weight || dp.requested?.weight } : null;
    }).filter(Boolean);
  }, [request, selections]);

  const poleTotal = selectedPoles.reduce((s, p) => s + getPolePrice(p.length), 0);
  const insuranceTotal = includeInsurance ? selectedPoles.length * INSURANCE_PRICE : 0;
  const monthlyTotal = poleTotal + insuranceTotal;

  const canSubmit = request?.desired_poles?.every((dp, idx) => {
    if (dp.exact_available) return true;
    return selections[idx] !== null && selections[idx] !== undefined;
  }) ?? false;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const selectionsArray = Object.entries(selections)
        .filter(([_, poleId]) => poleId !== null && poleId !== -1)
        .map(([index, poleId]) => ({ pole_index: parseInt(index), selected_pole_id: poleId }));

      await axios.post(`${API}/pole-requests/${requestId}/submit-alternates`, {
        original_request_id: parseInt(requestId),
        selections: selectionsArray
      });
      toast.success("Poles confirmed!");
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit selections");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Poles Confirmed!</h2>
            <p className="text-slate-600 mb-6">
              Your pole selections have been submitted. Make sure you've also completed the other steps from your email:
            </p>
            <div className="text-left bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-emerald-600">
                <Check className="w-4 h-4" /> <span className="font-medium">Select Poles — Done!</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <span className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0" /> Add Payment Method
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <span className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0" /> Sign Rental Agreement
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-4">Check your email for the links to complete the remaining steps.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Select Your Poles
          </h1>
          <p className="text-slate-600">Request #{requestId} - {request?.requester_name}</p>
        </div>

        {/* User info */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">{request?.requester_name}</CardTitle>
                <CardDescription>{request?.requester_email}</CardDescription>
              </div>
            </div>
          </CardHeader>
          {request?.rental_start_date && (
            <CardContent className="pt-0">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Calendar className="w-4 h-4" />
                <span>Start: {new Date(request.rental_start_date).toLocaleDateString()}</span>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Pole selection */}
        <div className="space-y-4">
          {request?.desired_poles?.map((dp, idx) => (
            <Card key={idx}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    Requested: {dp.length || dp.requested?.length} - {dp.weight || dp.requested?.weight}
                  </CardTitle>
                  {dp.exact_available ? (
                    <Badge variant="default" className="bg-emerald-500">Available</Badge>
                  ) : dp.similar_matches?.length > 0 ? (
                    <Badge variant="secondary">Alternatives</Badge>
                  ) : (
                    <Badge variant="outline">Checking</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {dp.exact_available ? (
                  <div className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-md">
                    <Check className="w-4 h-4 inline mr-2" />
                    Available! {dp.exact_matches?.[0] && `(${dp.exact_matches[0].brand})`}
                  </div>
                ) : dp.similar_matches?.length > 0 ? (
                  <RadioGroup value={selections[idx]?.toString() || ''} onValueChange={(val) => setSelections(prev => ({ ...prev, [idx]: parseInt(val) }))}>
                    {dp.similar_matches.map((pole, pIdx) => (
                      <div key={pIdx} className={`flex items-center space-x-3 p-3 rounded-md border transition-colors ${selections[idx] === pole.pole_id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                        <RadioGroupItem value={pole.pole_id.toString()} id={`pole-${idx}-${pIdx}`} />
                        <Label htmlFor={`pole-${idx}-${pIdx}`} className="flex-1 cursor-pointer">
                          <span className="font-semibold">{pole.length} - {pole.weight}</span>
                          <span className="text-slate-500 ml-2">({pole.brand})</span>
                        </Label>
                      </div>
                    ))}
                    <div className={`flex items-center space-x-3 p-3 rounded-md border ${selections[idx] === -1 ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                      <RadioGroupItem value="-1" id={`pole-${idx}-none`} />
                      <Label htmlFor={`pole-${idx}-none`} className="flex-1 cursor-pointer text-amber-700">
                        None of these work - please contact me
                      </Label>
                    </div>
                  </RadioGroup>
                ) : (
                  <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-md">
                    <Package className="w-4 h-4 inline mr-2" />
                    We're checking availability for this pole.
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Price summary */}
        {selectedPoles.length > 0 && (
          <Card className="mt-6">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Estimated Monthly Price
              </h3>
              <div className="space-y-2 text-sm">
                {selectedPoles.map((pole, idx) => (
                  <div key={idx} className="flex justify-between text-slate-600">
                    <span>{pole.length} - {pole.weight}</span>
                    <span>${getPolePrice(pole.length).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2">
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Checkbox id="insurance" checked={includeInsurance} onCheckedChange={setIncludeInsurance} />
                      <Label htmlFor="insurance" className="text-slate-600 cursor-pointer flex items-center gap-1">
                        <Shield className="w-3 h-3" /> Insurance ($10/pole/mo)
                      </Label>
                    </div>
                    <span className={includeInsurance ? "text-slate-600" : "text-slate-400 line-through"}>
                      ${insuranceTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold text-slate-900 text-base">
                  <span>Total Monthly</span>
                  <span>${monthlyTotal.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-8 flex justify-center">
          <Button size="lg" onClick={handleSubmit} disabled={!canSubmit || submitting} className="min-w-[240px]">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : <><Check className="w-4 h-4 mr-2" />Confirm Pole Selections</>}
          </Button>
        </div>
        <p className="text-center text-sm text-slate-500 mt-4">
          Having trouble? Reply to the email or contact us directly.
        </p>
      </div>
    </div>
  );
}
